import { createNetworkRequestHook } from '@/utils/createNetworkRequestHook'
import { createPaginatedNetworkRequestHook } from '@/utils/createPaginatedNetworkRequestHook'

import {
  ActiveSharesPayload,
  ActiveSharesResponse,
  AssetActiveSharesPayload,
  CancelActiveSharesPayload,
  CancelActiveSharesResponse,
  Charge,
  ExtendActiveShareId,
  HistorySharesPayload,
  HistorySharesResponse,
  OrganizationChargesPayload,
  OrganizationChargesResponse,
  PostSharesPayload,
  PostSharesResponse,
  PostShareValidatePayload,
  Share,
  ShareChargesPayload,
  ShareId,
  ShareValidationResponse,
  SinglePostSharesPayload,
  SinglePostSharesResponse,
  UpdateMapAccessPayload,
  UpdateMapAccessResponse,
  UpdateShareReferencePayload,
  UpdateShareReferenceResponse,
} from '@/lib/features/SmartShares/interfaces'

import { useNetworkMutation, useNetworkRequest } from '@/lib/utils/request'

const baseUrl = 'v1/smart-shares'

export const useShares = createPaginatedNetworkRequestHook<
  Share,
  HistorySharesPayload,
  HistorySharesResponse
>(`${baseUrl}/`)

export const useOrganizationCharges = (params: OrganizationChargesPayload | null) =>
  useNetworkRequest<
    OrganizationChargesResponse,
    unknown,
    { fallbackData: OrganizationChargesResponse }
  >(
    params
      ? {
          url: `${baseUrl}/charges/${params.organizationId}`,
          config: {
            params,
          },
        }
      : null,
    { fallbackData: [] },
  )

export const useActiveShares = createPaginatedNetworkRequestHook<
  Share,
  ActiveSharesPayload | undefined,
  ActiveSharesResponse
>(`${baseUrl}/active/`)

export const useAssetActiveShares = createNetworkRequestHook<
  ActiveSharesResponse,
  AssetActiveSharesPayload
>(`${baseUrl}/active/`)

export const useHistorySmartShares = createPaginatedNetworkRequestHook<
  Share,
  ActiveSharesPayload,
  HistorySharesResponse
>(`${baseUrl}/history/`)

export const useShareCharges = createPaginatedNetworkRequestHook<Charge, ShareChargesPayload>(
  `${baseUrl}/charges/`,
)

export const useSmartShareValidation = () =>
  useNetworkMutation<ShareValidationResponse, PostShareValidatePayload[]>({
    url: `${baseUrl}/validate/`,
    method: 'post',
  })

export const useSharesSharedFromMe = createPaginatedNetworkRequestHook<Share>(
  `${baseUrl}/active/shared-from-me/`,
)

export const useSharesSharedToMe = createPaginatedNetworkRequestHook<Share>(
  `${baseUrl}/active/shared-to-me/`,
)

export const useCancelSmartShare = () =>
  useNetworkMutation<CancelActiveSharesResponse, { params: { id: CancelActiveSharesPayload } }>({
    url: `${baseUrl}/\${params.id}/cancel/`,
    method: 'post',
  })

export const useUpdateSmartShareMapAccess = () =>
  useNetworkMutation<
    UpdateMapAccessResponse,
    { params: { id: number }; data: UpdateMapAccessPayload['data'] }
  >({
    url: `${baseUrl}/\${params.id}/map-access/`,
    method: 'patch',
  })

/**
 * Partial update of a single active share's reference. Shares the `PATCH /v1/smart-shares/{id}/`
 * endpoint with `useUpdateShareAssets`, but sends the reference on its own so the share period,
 * asset and organisation are left untouched.
 */
export const useUpdateShareReference = () =>
  useNetworkMutation<
    UpdateShareReferenceResponse,
    { params: { id: ShareId }; data: UpdateShareReferencePayload['data'] }
  >({
    url: `${baseUrl}/\${params.id}/`,
    method: 'patch',
  })

export const useUpdateShareAssets = () =>
  useNetworkMutation<
    SinglePostSharesResponse,
    { params: { id: ExtendActiveShareId }; data: SinglePostSharesPayload }
  >({
    url: `${baseUrl}/\${params.id}/`,
    method: 'patch',
  })

export const useShareAssets = () =>
  useNetworkMutation<PostSharesResponse, PostSharesPayload>({
    url: `${baseUrl}/`,
    method: 'post',
  })
